// Theme Context for Leonardo Color Management
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useEffect, useCallback } from 'react';
import type { Theme } from '@adobe/leonardo-contrast-colors';
import type { SimpleThemeConfig } from '@/utils/leonardo/theme';
import { BASE_COLORS } from '@/utils/leonardo/constants';
import { getViteMetaEnv } from '@/utils/viteMetaEnv';
import { useThemeMode } from './ThemeModeContext';
import { useIdleLoad } from '@/utils/useIdleLoad';

export interface ThemeContextValue {
  theme: Theme | null;
  config: SimpleThemeConfig;
  updateConfig: (newConfig: Partial<SimpleThemeConfig>) => void;
  regenerateTheme: () => void;
  syncWithSystemPreference: boolean;
  setSyncWithSystemPreference: (sync: boolean) => void;
  resetToSystemPreference: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: React.ReactNode;
  initialConfig?: Partial<SimpleThemeConfig>;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  initialConfig = {} 
}) => {
  // Connect to ThemeModeContext for dark/light mode awareness
  // Note: ThemeProvider must be nested inside ThemeModeProvider for this to work
  const { mode } = useThemeMode();
  
  // Track whether to sync with system preference
  const [syncWithSystemPreference, setSyncWithSystemPreference] = useState(true);
  
  // Initialize config with mode-aware defaults
  const [config, setConfig] = useState<SimpleThemeConfig>(() => {
    if (mode === 'dark') {
      return {
        lightness: 15,
        contrast: 1,
        saturation: 100,
        backgroundColor: 'Black',
        ...initialConfig,
      };
    }
    return {
      lightness: 100,
      contrast: 1,
      saturation: 100,
      backgroundColor: 'White',
      ...initialConfig,
    };
  });
  
  const [theme, setTheme] = useState<Theme | null>(null);
  const engineReady = useIdleLoad(2000);

  // NOTE: Static CSS variables are handled entirely by CSS now.
  // _theme-aliases.scss maps dynamic vars (--color-black-*) to static vars
  // (--color-light-black-* / --color-dark-black-*) based on the .dark-mode /
  // .light-mode class set by the blocking script in index.html.
  // No JavaScript injection needed for initial styling.

  // Sync with system preference when mode changes (only if sync is enabled)
  // Note: We don't include initialConfig in deps because it's only used on mount
  useEffect(() => {
    if (!syncWithSystemPreference) return;
    
    // Generate config based on current mode (don't use initialConfig here)
    const defaultConfig = mode === 'dark' 
      ? {
          lightness: 15,
          contrast: 1,
          saturation: 100,
          backgroundColor: 'Black' as const,
        }
      : {
          lightness: 100,
          contrast: 1,
          saturation: 100,
          backgroundColor: 'White' as const,
        };
    
    setConfig(defaultConfig);
    
    // Debug logging
    if (getViteMetaEnv().DEV) {
      // eslint-disable-next-line no-console
      console.log(`🎨 Theme synced with ${mode} mode:`, {
        backgroundColor: defaultConfig.backgroundColor,
        lightness: defaultConfig.lightness,
      });
    }
  }, [mode, syncWithSystemPreference]); // Removed initialConfig from deps!

  // Update configuration and regenerate theme
  // This is called when user manually adjusts controls
  const updateConfig = useCallback((newConfig: Partial<SimpleThemeConfig>) => {
    // If user is manually changing lightness or backgroundColor, disable sync
    if ('lightness' in newConfig || 'backgroundColor' in newConfig) {
      setSyncWithSystemPreference(false);
      
      // Debug logging
      if (getViteMetaEnv().DEV) {
        // eslint-disable-next-line no-console
        console.log('🎛️ User override detected - sync disabled', newConfig);
      }
    }
    
    setConfig((prev) => ({
      ...prev,
      ...newConfig,
    }));
  }, []);

  // Reset to system preference
  const resetToSystemPreference = useCallback(() => {
    setSyncWithSystemPreference(true);
    const defaultConfig = mode === 'dark'
      ? {
          lightness: 15,
          contrast: 1,
          saturation: 100,
          backgroundColor: 'Black' as const,
        }
      : {
          lightness: 100,
          contrast: 1,
          saturation: 100,
          backgroundColor: 'White' as const,
        };
    setConfig(defaultConfig);
  }, [mode]); // Removed initialConfig

  // Regenerate theme when config changes (simplified, no useCallback needed)
  const regenerateTheme = useCallback(() => {
    // This will be handled by the lazy-loaded engine
    // We just trigger a config update to force a re-render
    setConfig(prev => ({ ...prev }));
  }, []);

  // Regenerate theme and inject CSS variables when config changes
  useEffect(() => {
    if (!engineReady) return;

    let isMounted = true;

    const loadEngineAndGenerate = async () => {
      // Dynamically import Leonardo engine
      const { 
        createTheme, 
        createStaticTheme, 
        injectCSSVariables, 
        injectStaticCSSVariables,
        printAvailableVariables
      } = await import('@/utils/leonardo');

      if (!isMounted) return;

      const newTheme = createTheme(config, mode);
      setTheme(newTheme);
      
      // ALWAYS inject base colors to ensure dynamic single-source behavior
      // Use custom base colors if provided, otherwise use defaults from BASE_COLORS
      const baseColorsToInject = config.baseColors || {
        black: BASE_COLORS.black,
        red: BASE_COLORS.red,
        orange: BASE_COLORS.orange,
        blue: BASE_COLORS.blue,
      };
      
      // Inject dynamic "Hero" variables
      injectCSSVariables(newTheme, baseColorsToInject);

      // Inject STATIC scales (Light/Dark modes) based on current base colors
      // This enables the rest of the UI (footer, headers) to update with the random theme
      // while maintaining their semantic roles (light/dark mode)
      
      // 1. Generate Light Mode Static Theme with current base colors
      const lightStaticTheme = createStaticTheme('light', baseColorsToInject);
      injectStaticCSSVariables(lightStaticTheme, 'light');

      // 2. Generate Dark Mode Static Theme with current base colors
      const darkStaticTheme = createStaticTheme('dark', baseColorsToInject);
      injectStaticCSSVariables(darkStaticTheme, 'dark');
      
      // Debug: Print available CSS variables to console
      if (getViteMetaEnv().DEV) {
        printAvailableVariables(newTheme);
        // Debug: Log raw Leonardo output keys
        // eslint-disable-next-line no-console
        console.log('🔍 Raw Leonardo contrastColorPairs keys:', Object.keys(newTheme.contrastColorPairs));
      }
    };

    loadEngineAndGenerate();

    return () => {
      isMounted = false;
    };
  }, [config, mode, engineReady]);

  const value: ThemeContextValue = {
    theme,
    config,
    updateConfig,
    regenerateTheme,
    syncWithSystemPreference,
    setSyncWithSystemPreference,
    resetToSystemPreference,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
